import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

const NotifCtx = createContext(null);
const STORE_KEY = "bj_notifications";
const REMINDED_KEY = "bj_reminded";
const POLL_MS = 30000;

function load() {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
  } catch {
    return [];
  }
}

function loadReminded() {
  try {
    return new Set(JSON.parse(localStorage.getItem(REMINDED_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState(load);
  const lastStatuses = useRef({});
  const remindedToday = useRef(loadReminded());

  useEffect(() => {
    localStorage.setItem(STORE_KEY, JSON.stringify(notifications.slice(0, 50)));
  }, [notifications]);

  const push = useCallback((n) => {
    setNotifications((prev) => [
      { id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, read: false, at: new Date().toISOString(), ...n },
      ...prev,
    ]);
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const clearAll = useCallback(() => setNotifications([]), []);

  useEffect(() => {
    if (!user || user.role !== "customer") return;
    let active = true;

    async function poll() {
      try {
        const orders = await api.lookupOrders({ mine: true });
        if (!active) return;
        for (const o of orders) {
          const prev = lastStatuses.current[o.id];
          if (prev && prev !== o.status && o.status !== "rejected") {
            toast(`Order ${o.order_number} is now ${o.status}`, { description: "Tap My Orders to view details." });
            push({ type: "status", order_id: o.id, title: `Order ${o.order_number} updated`, body: `Status: ${o.status}` });
          }
          lastStatuses.current[o.id] = o.status;
        }
      } catch (e) {
        /* silent */
      }
      try {
        const res = await api.getBalanceDueReminders(24);
        if (!active) return;
        const dayKey = new Date().toISOString().slice(0, 10);
        for (const r of res.reminders || []) {
          const key = `${r.order_id}-${dayKey}`;
          if (remindedToday.current.has(key)) continue;
          remindedToday.current.add(key);
          try {
            localStorage.setItem(REMINDED_KEY, JSON.stringify([...remindedToday.current].slice(-100)));
          } catch (e) {
            /* localStorage unavailable */
          }
          const msg = r.is_overdue
            ? `Balance overdue for order ${r.order_number}`
            : `Balance due soon for order ${r.order_number}`;
          toast(msg, { description: `$${Number(r.balance_due).toFixed(2)} remaining — pay to complete.` });
          push({ type: "balance", order_id: r.order_id, title: msg, body: `$${Number(r.balance_due).toFixed(2)} balance due` });
        }
      } catch (e) {
        /* silent */
      }
    }

    poll();
    const id = setInterval(poll, POLL_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [user, push]);

  const unread = notifications.filter((n) => !n.read).length;

  return (
    <NotifCtx.Provider value={{ notifications, unread, markAllRead, clearAll, push }}>
      {children}
    </NotifCtx.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotifCtx);
  if (!ctx) throw new Error("useNotifications must be used within NotificationsProvider");
  return ctx;
}
